import type React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { MentionCandidate } from "@/lib/humhub-api/types";
import { isStudyGroupCandidate } from "@/lib/utils/mentionUtils";

interface MentionPosition {
  top: number;
  left: number;
}

interface MentionSuggestionsProps {
  isVisible: boolean;
  position: MentionPosition | null;
  candidates: MentionCandidate[];
  selectedIndex: number;
  isLoading: boolean;
  onSelect: (item: MentionCandidate) => void;
  onClose: () => void;
  suggestionsRef: React.RefObject<HTMLDivElement | null>;
}

export const MentionSuggestions: React.FC<MentionSuggestionsProps> = ({
  isVisible,
  position,
  candidates,
  selectedIndex,
  isLoading,
  onSelect,
  onClose,
  suggestionsRef,
}) => {
  const { t } = useTranslation();
  // キーボードショートカットの処理
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isVisible) return;

      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isVisible, onClose]);

  // 選択されたアイテムを表示領域内にスクロール
  useEffect(() => {
    if (isVisible && suggestionsRef.current && candidates.length > 0) {
      const selectedElement = suggestionsRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex, isVisible, candidates.length, suggestionsRef]);

  if (!isVisible || !position) {
    return null;
  }

  const handleItemClick = (item: MentionCandidate) => {
    onSelect(item);
  };

  return (
    <div
      ref={suggestionsRef}
      className="fixed z-50 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        width: "300px",
      }}
    >
      {isLoading ? (
        <div className="p-3 text-center text-gray-500">
          <div className="mr-2 inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent text-blue-600" />
          {t("common.loading")}
        </div>
      ) : candidates.length === 0 ? (
        <div className="p-3 text-center text-gray-500">
          {t("common.noMatchesFound")}
        </div>
      ) : (
        <div className="py-1">
          {candidates.map((item, index) => {
            const isSelected = index === selectedIndex;
            const isGroup = isStudyGroupCandidate(item);

            return (
              <div
                key={item.id}
                className={`flex cursor-pointer items-center gap-3 px-3 py-2 transition-colors ${
                  isSelected
                    ? isGroup
                      ? "bg-green-50 text-green-900"
                      : "bg-blue-50 text-blue-900"
                    : "hover:bg-gray-50"
                }`}
                onClick={() => handleItemClick(item)}
              >
                <Avatar className="size-8 shrink-0">
                  <AvatarImage src={item.avatarUrl} alt={item.displayName} />
                  <AvatarFallback
                    className={
                      isGroup
                        ? "bg-green-100 text-green-700"
                        : "bg-blue-100 text-blue-700"
                    }
                  >
                    {item.displayName?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium">
                      {item.displayName}
                    </span>
                  </div>
                  {item.secondaryText && (
                    <span className="truncate text-xs text-gray-500">
                      {item.secondaryText}
                    </span>
                  )}
                </div>

                <div className="shrink-0">
                  {isGroup ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
                      {t("common.group")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                      {t("common.user")}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
